/** A session agent can ask, but only its owner can sign a project grant. The
 * request lives in the owner's private World, never in the editable thread. */
import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { WorldDoc } from '@mod/core/model'
import { useState } from 'react'
import { useSharingAccount } from '../../services/sharing/accountContext'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useDocument } from '../../services/world/repo/useDocument'

export function ProjectAccessApproval({ sessionId }: { sessionId: string }) {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  const [world] = useDocument<WorldDoc>(
    account?.opened.location.worldId as AnyDocumentId | undefined,
  )
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const requests = Object.entries(
    world?.privateSessions?.[sessionId]?.projectAccessRequests ?? {},
  ).filter(([, request]) => request.status === 'pending')
  if (!account || !sharing || !world || !requests.length) return null
  const namedWorld = world

  const decide = async (id: string, choice: 'approve' | 'deny') => {
    setBusy(id)
    setError('')
    try {
      const { decideSessionProjectAccess } = await import('@mod/core/sharing')
      await decideSessionProjectAccess(
        sharing,
        {
          person: account.opened.location.personId,
          worldId: account.opened.location.worldId,
        },
        sessionId,
        id,
        choice,
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not decide')
    } finally {
      setBusy('')
    }
  }

  return (
    <section
      className="mx-auto w-full max-w-3xl space-y-2 px-3 pb-2"
      aria-label="Project access requests"
    >
      {requests.map(([id, request]) => {
        const name = String(
          namedWorld.workspaces?.[`automerge:${String(request.projectId)}`]
            ?.displayName ?? request.projectId,
        )
        return (
          <div
            key={id}
            className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
          >
            <p>
              This session’s agent asked for{' '}
              {request.access === 'edit' ? 'Edit' : 'Read'} on{' '}
              <span className="font-medium">{name}</span>. It cannot use the
              project while this is pending.
            </p>
            <div className="mt-2 flex gap-1.5">
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void decide(id, 'approve')}
                className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-muted"
              >
                {busy === id ? '…' : 'Approve'}
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void decide(id, 'deny')}
                className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-muted"
              >
                Deny
              </button>
            </div>
          </div>
        )
      })}
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </section>
  )
}
